<script setup>
import { useSlots } from "vue";

import SvgIcon from "../SvgIcon.vue";

defineProps({
  icon: { type: String, default: undefined },
  // When set, the title (icon + text) becomes a link to this route.
  titleTo: { type: [Object, String], default: undefined },
});

const slots = useSlots();
</script>

<template>
  <section class="widget-card">
    <header class="widget-card-header">
      <h2 class="widget-card-title">
        <RouterLink v-if="titleTo" :to="titleTo" class="widget-card-title-link">
          <SvgIcon v-if="icon" :name="icon" width="18" height="18" aria-hidden="true" />
          <span><slot name="title" /></span>
        </RouterLink>
        <template v-else>
          <SvgIcon v-if="icon" :name="icon" width="18" height="18" aria-hidden="true" />
          <span><slot name="title" /></span>
        </template>
      </h2>
      <div v-if="slots.actions" class="widget-card-actions"><slot name="actions" /></div>
    </header>
    <div class="widget-card-body"><slot /></div>
    <footer v-if="slots.footer" class="widget-card-footer"><slot name="footer" /></footer>
  </section>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;
@use "@/styles/mixins/typography";

.widget-card {
  display: flex;
  flex-direction: column;
  background: c.$background-ui;
  border: 1px solid c.$border-light;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px p.$black-alpha-5;
  height: 320px;
}

.widget-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px 6px;
}

.widget-card-title {
  @include typography.large("semi-bold");

  color: c.$main;
  margin: 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.widget-card-title-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: inherit;
  text-decoration: none;
  transition: color 0.15s ease;
}

.widget-card-title-link:hover {
  color: c.$main-hover;
}

.widget-card-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.widget-card-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.widget-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid c.$border-light;
}
</style>
